// =============================================================================
// MobileAddOrder.tsx
// Full-screen New / Edit Order sheet on mobile.
//
// Design system: every field uses the shared `.input` / `.field-label` /
// `.btn` / `.btn-ghost` / `.btn-primary` classes from components.css. The
// Department and Category pickers go through the <Dropdown> primitive (which
// portals into document.body, so it escapes any `overflow: hidden` parent and
// matches the desktop dropdown behaviour). The duplicate-order prompt uses
// the mobile-native <ConfirmModal variant="sheet">.
// =============================================================================

import React, { useEffect, useMemo, useState } from 'react';

import { Dropdown, ConfirmModal } from '../ui';

import { createOrder, updateOrderField } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';

import { Order, OrderStatus, Department, StaffMember } from '../../lib/types';
import type { Vendor } from '../../lib/types';

import { S } from './mobileStyles';
import { StaffField }    from './addorder/StaffField';
import { LineItemRow, type FormItem } from './addorder/LineItemRow';

// ── TYPES ────────────────────────────────────────────────────────────

interface MobileAddOrderProps {
  show: boolean;
  onClose: () => void;
  editingOrder: Order | null;
  departments: Department[];
  staffDirectory: StaffMember[];
  categories: string[];
  vendors: Vendor[];
  onOrderAdded: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}

interface DuplicateConfirm {
  message: string;
  payload: any;
}

// ── HELPERS ──────────────────────────────────────────────────────────

const emptyItem = (): FormItem => ({ item_name: '', quantity: 1, unit_price: 0, size: '', sku: '' });

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileAddOrder({
  show, onClose, editingOrder, departments, staffDirectory, categories, vendors,
  onOrderAdded, showToast,
}: MobileAddOrderProps) {

  // Form state
  const [formDate,     setFormDate]     = useState(() => new Date().toISOString().split('T')[0]);
  const [formStaff,    setFormStaff]    = useState('');
  const [formDept,     setFormDept]     = useState('');
  const [formVendor,   setFormVendor]   = useState('');
  const [formCategory, setFormCategory] = useState('');
  const [formOrderNum, setFormOrderNum] = useState('');
  const [formNotes,    setFormNotes]    = useState('');
  const [formItems,    setFormItems]    = useState<FormItem[]>([emptyItem()]);

  // UI state
  const [busy,       setBusy]       = useState(false);
  const [dupConfirm, setDupConfirm] = useState<DuplicateConfirm | null>(null);

  const isEdit = !!editingOrder;

  // Populate form when editing — reset when mounting fresh.
  useEffect(() => {
    if (editingOrder) {
      setFormDate(editingOrder.order_date || new Date().toISOString().split('T')[0]);
      setFormStaff(editingOrder.staff_name || '');
      setFormDept(editingOrder.department_id || '');
      setFormVendor(editingOrder.vendor || '');
      setFormCategory(editingOrder.category || '');
      setFormOrderNum(editingOrder.order_number || '');
      setFormNotes(editingOrder.notes || '');
      setFormItems(editingOrder.order_items?.length
        ? editingOrder.order_items.map(i => ({
            item_name: i.item_name || '',
            quantity:  i.quantity || 1,
            unit_price: i.unit_price || 0,
            size: i.size || '',
            sku:  i.code || '',
          }))
        : [emptyItem()]
      );
    } else {
      setFormDate(new Date().toISOString().split('T')[0]);
      setFormStaff(''); setFormDept(''); setFormVendor(''); setFormCategory('');
      setFormOrderNum(''); setFormNotes(''); setFormItems([emptyItem()]);
    }
  }, [editingOrder, show]);

  // Derived
  const formTotal = useMemo(
    () => formItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0),
    [formItems],
  );

  const deptOptions = useMemo(
    () => [{ value: '', label: 'Select…' }, ...departments.map(d => ({ value: d.id, label: d.name }))],
    [departments],
  );

  const categoryOptions = useMemo(
    () => [{ value: '', label: 'Select…' }, ...categories.map(c => ({ value: c, label: c }))],
    [categories],
  );

  // Item-row mutators
  const updateItem = (idx: number, field: keyof FormItem, value: string | number) => {
    setFormItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };
  const addItemRow    = () => setFormItems(prev => [...prev, emptyItem()]);
  const removeItemRow = (idx: number) => {
    if (formItems.length <= 1) return;
    setFormItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Save handler — separate paths for edit and create.
  const handleSave = async () => {
    if (!formDept)    { showToast('Please select a department'); return; }
    if (!formCategory) { showToast('Please select a category'); return; }
    if (formItems.every(i => !i.item_name.trim())) { showToast('Please add at least one item'); return; }
    setBusy(true);

    if (isEdit && editingOrder) {
      const updates: Array<{ field: string; value: string | number }> = [];
      if (formDate     !== (editingOrder.order_date   || '')) updates.push({ field: 'date_ordered', value: formDate     });
      if (formStaff    !== (editingOrder.staff_name   || '')) updates.push({ field: 'staff_name',   value: formStaff    });
      if (formVendor   !== (editingOrder.vendor       || '')) updates.push({ field: 'vendor_name',  value: formVendor   });
      if (formOrderNum !== (editingOrder.order_number || '')) updates.push({ field: 'order_number', value: formOrderNum });
      if (formNotes    !== (editingOrder.notes        || '')) updates.push({ field: 'notes',        value: formNotes    });
      if (formCategory !== (editingOrder.category     || '')) updates.push({ field: 'category',     value: formCategory });
      if (updates.length === 0) { showToast('No changes to save', 'success'); onClose(); setBusy(false); return; }

      // Thread updated_at forward — every successful update bumps the
      // server-side timestamp, so later calls must send the refreshed
      // value or they'll be rejected as VERSION_CONFLICT.
      let failed = 0;
      let expectedUpdatedAt = editingOrder.updated_at;
      for (const u of updates) {
        const result = await updateOrderField(editingOrder.id, u.field, u.value, expectedUpdatedAt);
        if (!result.ok) { failed++; continue; }
        if (result.data?.updated_at) expectedUpdatedAt = result.data.updated_at;
      }
      if (failed === 0) { showToast('Order updated', 'success'); onClose(); onOrderAdded(); }
      else { showToast(`${failed} field(s) failed to update`); }
    } else {
      const validItems = formItems.filter(i => i.item_name.trim());
      const payload = {
        order_date: formDate, date_ordered: formDate, staff_name: formStaff,
        department_id: formDept, department_name: departments.find(d => d.id === formDept)?.name || '',
        vendor: formVendor, vendor_name: formVendor, order_number: formOrderNum || undefined,
        status: 'Pending' as OrderStatus, category: formCategory,
        total_cost: formTotal, notes: formNotes || undefined, source: 'manual',
        order_items: validItems.map(i => ({
          id: '', order_id: '', item_name: i.item_name.trim(), quantity: i.quantity,
          unit_price: i.unit_price || undefined, unit_cost: i.unit_price || undefined,
          size: i.size || undefined, code: i.sku || undefined, sku: i.sku || undefined, category: formCategory,
        })),
        items: validItems.map(i => ({
          item_name: i.item_name.trim(), quantity: i.quantity, unit_cost: i.unit_price || 0,
          size: i.size || undefined, sku: i.sku || undefined,
        })),
      };
      const result = await createOrder(payload as any);
      if (result.ok) { showToast('Order added', 'success'); onClose(); onOrderAdded(); }
      else if (result.error === 'DUPLICATE_ORDER') {
        // Hand control to the ConfirmModal — busy stays true until the user
        // decides. Cancel/confirm both clear busy and the modal state.
        setDupConfirm({
          message: `${result.message || 'A similar order already exists.'} Add it anyway?`,
          payload,
        });
        return;
      } else { showToast(result.message || result.error || 'Failed to add order'); }
    }
    setBusy(false);
  };

  const handleForceCreate = async () => {
    if (!dupConfirm) return;
    const { payload } = dupConfirm;
    setDupConfirm(null);
    const forceResult = await createOrder({ ...payload, force: true } as any);
    if (forceResult.ok) { showToast('Order added', 'success'); onClose(); onOrderAdded(); }
    else { showToast(forceResult.message || 'Failed to add order'); }
    setBusy(false);
  };

  const handleCancelDuplicate = () => {
    setDupConfirm(null);
    setBusy(false);
  };

  if (!show) return null;

  return (
    <>
      <div style={S.fullSheet} className="m-full-sheet">

        {/* Safe area */}
        <div style={{ ...S.safe, background: 'var(--surface)', flexShrink: 0 }} />

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 16px', height: 48, background: 'var(--surface)',
          borderBottom: '0.5px solid var(--border)', flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{ fontSize: 15, color: 'var(--accent)', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
          >Cancel</button>
          <span style={{ fontSize: 16, fontWeight: 600 }}>{isEdit ? 'Edit order' : 'New order'}</span>
          <button
            onClick={handleSave}
            disabled={busy}
            style={{ fontSize: 15, color: 'var(--accent)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', opacity: busy ? 0.5 : 1 }}
          >{busy ? 'Saving…' : 'Save'}</button>
        </div>

        {/* Scrollable form */}
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '12px 16px', paddingBottom: 40 }}>

          {/* Date */}
          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Date</label>
            <input type="date" value={formDate} onChange={e => setFormDate(e.target.value)} className="input" />
          </div>

          {/* Ordered for (staff type-to-search) */}
          <div style={{ marginBottom: 12 }}>
            <StaffField
              value={formStaff}
              onChange={setFormStaff}
              staffDirectory={staffDirectory}
            />
          </div>

          {/* Department + Category */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="field-label">Department</label>
              <Dropdown
                value={formDept}
                options={deptOptions}
                onChange={setFormDept}
                width="100%"
                placeholder="Select…"
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="field-label">Category</label>
              <Dropdown
                value={formCategory}
                options={categoryOptions}
                onChange={setFormCategory}
                width="100%"
                placeholder="Select…"
              />
            </div>
          </div>

          {/* Vendor + Reference # */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="field-label">Vendor</label>
              <input
                value={formVendor}
                onChange={e => setFormVendor(e.target.value)}
                placeholder="Vendor name"
                className="input"
                list="mobile-vendor-list"
              />
              <datalist id="mobile-vendor-list">
                {vendors.map(v => <option key={v.id} value={v.name} />)}
              </datalist>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="field-label">Reference #</label>
              <input
                value={formOrderNum}
                onChange={e => setFormOrderNum(e.target.value)}
                placeholder="Optional"
                className="input"
              />
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 16 }}>
            <label className="field-label">Notes</label>
            <input
              value={formNotes}
              onChange={e => setFormNotes(e.target.value)}
              placeholder="Optional notes…"
              className="input"
            />
          </div>

          {/* Line items */}
          <div style={{ marginBottom: 12 }}>
            <label className="field-label">Line items</label>
            <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: 10 }}>
              {formItems.map((item, idx) => (
                <LineItemRow
                  key={idx}
                  item={item}
                  index={idx}
                  total={formItems.length}
                  onChange={updateItem}
                  onRemove={removeItemRow}
                />
              ))}
              <button
                onClick={addItemRow}
                className="btn btn-ghost"
                style={{ width: '100%', marginTop: 10, color: 'var(--accent)', fontWeight: 500 }}
              >+ Add item</button>
            </div>
          </div>

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 4px', borderTop: '0.5px solid var(--border)' }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Total</span>
            <span style={{ fontSize: 17, fontWeight: 700 }}>{formatCurrency(formTotal, '$')}</span>
          </div>
        </div>
      </div>

      {dupConfirm && (
        <ConfirmModal
          open
          variant="sheet"
          title="Duplicate order?"
          message={dupConfirm.message}
          confirmLabel="Add anyway"
          onConfirm={handleForceCreate}
          onCancel={handleCancelDuplicate}
        />
      )}
    </>
  );
}
